import React, { useMemo, useState } from 'react';
import BasicButton from './BasicButton';
import TextInput from './TextInput';
import CheckBox from './CheckBox';
import EndPoint from 'Api/EndPoint';
import { BLANK_BUSINESS_NUMBER, joinError, PHONE_EXISTS, BUSINESS_NUMBER_EXISTS, WRONG_FORMAT_BUSINESS_NUMBER, WRONG_FORMAT_EMAIL } from 'Api/Errors';
import { useMutationQuery } from 'Api/hooks/useMutationQuery';
import { useSelector } from 'react-redux';
import VerifiedPhoneInput from './VerifiedPhoneInput';
import VerificationBusinessNumberInput from './VerificationBusinessNumberInput';
import { useError } from 'Api/hooks/useError';
import { useIntl } from 'react-intl';
import UploadBusinessRegistrationView from './UploadBusinessRegistrationView';

/*****************************************************************************
 * TODO: - 가입 경로 선택 옵션:
 *   - 페이스북
 *   - 인스타그램
 *   - 지인 추천(직접입력)
 *   - 기타(직접입력)
 *
 * - 지인 추천이나 기타 선택시 추가 입력란 표시
 *****************************************************************************/

const DEBUG = import.meta.env.DEV;

const initData = () => ({
  businessNumber: '',
  password: '',
  companyName: '',
  managerName: '',
  agreePrivacyUsage: false,
  referral: '',
  email: '',
  imageBusinessRegistration: '',
});

const handlePolicyClick = locale => {
  const width = window.outerWidth / 1.5;
  const height = window.outerHeight / 1.5;
  const left = window.screenX + window.outerWidth / 6;
  const top = window.screenY + window.outerHeight / 6;
  window.open(
    `${EndPoint.HostRoot}/terms/${locale}/t3/`,
    'Policy',
    `location=no,toolbar=no,scrollbars=no,resizable=yes,status=no,menubar=no,width=${width}px,height=${height}px,top=${top}px,left=${left}px`
  );
};

const isBlank = value => value.trim() === '';

const defaultData = {
  isTester: DEBUG,
  agreeMarketing: false,
  joinFrom: 'SELF_WEB',
  businessType: 'INDIVIDUAL',
};

const IndividualJoinForm = ({ onCompleted, managerPhone, otpCode, isVi = false }) => {
  const { showErrorPopup } = useError();
  const { locale } = useSelector(state => state.settings.locale);
  const { country } = useSelector(state => state.settings);
  const [data, setData] = useState(initData);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState(null);
  const [isValidBusinessNumber, setIsValidBusinessNumber] = useState(false);
  const { businessNumber, password, companyName, managerName, referral, email, agreePrivacyUsage, imageBusinessRegistration } = data;
  const phoneCountry = useMemo(() => (country === 'ko' ? '82' : '84'), [country]);
  const { mutate } = useMutationQuery({
    method: 'post',
    url: '/auth/join',
    options: {
      onSuccess: () => {
        onCompleted({ phone: businessNumber, password, phoneCountry, companyName });
      },
      onError: error => {
        showErrorPopup({ text: error?.response?.data?.message });
      },
    },
  });
  const { formatMessage } = useIntl();
  const { mutate: checkDuplicatePhoneMutate } = useMutationQuery({
    method: 'post',
    url: '/auth/checkDuplicatePhone',
    options: {
      onSuccess: ({ data }) => {
        if (data.result) {
          setIsValidBusinessNumber(true);
        } else {
          setError(BUSINESS_NUMBER_EXISTS);
        }
      },
      onError: error => {
        showErrorPopup({ text: error?.response?.data?.message });
      },
    },
  });

  const handleDataChange = ({ target: { name, value } }) => {
    setError(null);
    setData(data => ({ ...data, [name]: value }));
  };

  const isEmail = value => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(value);
  };

  const handleJoin = () => {
    if (email && !isEmail(email)) return setError(WRONG_FORMAT_EMAIL);

    mutate({
      ...defaultData,
      otpCode,
      phoneCountry,
      businessNumber,
      companyName,
      managerName,
      managerPhone,
      agreePrivacyUsage,
      password,
      ...(!isVi && { imageBusinessRegistration }),
      ...(referral && { referral }),
      ...(email && { email }),
    });
  };

  const isDisabled = useMemo(() => {
    if (password.length < 6 || password.length > 15) return true;
    if (password !== confirmPassword) return true;
    if (!isVi && isBlank(imageBusinessRegistration)) return true;
    return isBlank(businessNumber) || isBlank(companyName) || isBlank(managerName) || !agreePrivacyUsage || !isValidBusinessNumber;
  }, [businessNumber, companyName, managerName, password, confirmPassword, agreePrivacyUsage, isValidBusinessNumber, imageBusinessRegistration, isVi]);

  const isError = (myErrorList = []) => {
    if (!error) return false;
    if (myErrorList.includes(error)) return true;
    return false;
  };

  const handleCheckBusinessNumber = () => {
    setIsValidBusinessNumber(false);
    if (isBlank(businessNumber)) return setError(BLANK_BUSINESS_NUMBER);
    if (businessNumber.length !== 10) return setError(WRONG_FORMAT_BUSINESS_NUMBER);
    checkDuplicatePhoneMutate({ phoneCountry, businessNumber });
  };

  return (
    <>
      <div className='form-container'>
        <VerifiedPhoneInput value={managerPhone} />
        <VerificationBusinessNumberInput
          isError={isError([BLANK_BUSINESS_NUMBER, PHONE_EXISTS, BUSINESS_NUMBER_EXISTS, WRONG_FORMAT_BUSINESS_NUMBER])}
          errorText={joinError[error] && formatMessage({ id: joinError[error] })}
          name='businessNumber'
          label='seller.businessNumber'
          placeholder='sign.phone.message'
          value={businessNumber}
          onValueChange={({ value }) => {
            setIsValidBusinessNumber(false);
            handleDataChange({ target: { name: 'businessNumber', value } });
          }}
          tabIndex={1}
          autoFocus
          isValidNumber={isValidBusinessNumber}
          handleCheckBusinessNumber={handleCheckBusinessNumber}
        />
        <TextInput name='companyName' label='recruitment.companyName' placeholder='recruitment.companyName.placeholder' value={companyName} onChange={handleDataChange} tabIndex={2} />
        <TextInput name='managerName' label='signIn.join.managerName2' placeholder='signIn.join.managerName.placeholder' value={managerName} onChange={handleDataChange} tabIndex={3} />
        <TextInput
          isError={password !== '' && (password.length < 6 || password.length > 15)}
          errorText={formatMessage({ id: 'error.password.validate' })}
          name='password'
          label='common.password'
          placeholder='sign.password.message'
          value={password}
          onChange={handleDataChange}
          isPassword
          tabIndex={4}
        />
        <TextInput
          isError={confirmPassword !== '' && confirmPassword !== password}
          errorText={formatMessage({ id: 'error.password.notMatch' })}
          name='confirmPassword'
          label='common.password2'
          value={confirmPassword}
          onChange={e => {
            setConfirmPassword(e.target.value);
            setError(null);
          }}
          isPassword
          tabIndex={5}
        />
        {!isVi && <UploadBusinessRegistrationView imageBusinessRegistration={imageBusinessRegistration} onChange={handleDataChange} />}
        <TextInput
          isError={isError([WRONG_FORMAT_EMAIL])}
          errorText={joinError[error] && formatMessage({ id: joinError[error] })}
          name='email'
          label='signIn.join.email'
          placeholder='signIn.join.email.placeholder'
          value={email}
          onChange={handleDataChange}
          tabIndex={6}
        />
        <TextInput name='referral' label='signIn.join.referral' placeholder='signIn.join.referral.placeholder' value={referral} onChange={handleDataChange} tabIndex={7} />
      </div>
      <CheckBox
        label='signIn.join.agreePrivacyUsage'
        isChecked={agreePrivacyUsage}
        onSetIsChecked={() => handleDataChange({ target: { name: 'agreePrivacyUsage', value: !agreePrivacyUsage } })}
        onClickExtra={() => handlePolicyClick(locale)}
      />
      <BasicButton label='signIn.join.button' onClick={handleJoin} style={{ backgroundColor: '#0567F9' }} disabled={isDisabled} />
    </>
  );
};

export default IndividualJoinForm;
